/* ==========================================================================
   Gemians - Bags Blog Style prelander

   Two jobs: reveal the sticky call to action once the reader reaches the
   trigger, and hold the two reel videos back until they are near the viewport.
   Vanilla, deferred, no dependency on the theme's runtime - this page does not
   load it.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* ------------------------------------------------------------------------
     Sticky call to action

     The source revealed it when its trigger paragraph crossed the middle of
     the screen: `rootMargin: '-50% 0px -50% 0px'`, i.e. a root one pixel tall
     across the centre. Same idea here, but the position is a setting, so the
     margin is built from it - 0.5 gives the source's behaviour exactly.
     ---------------------------------------------------------------------- */
  function initSticky() {
    var bar = document.querySelector('[data-gemians-blog-sticky]');
    if (!bar) return;

    function reveal() {
      bar.classList.add('gemians-blog-sticky--visible');
    }

    var selector = bar.getAttribute('data-trigger');
    var trigger = selector ? document.querySelector(selector) : null;

    // No trigger on the page (section removed, or the selector was mistyped):
    // reveal immediately rather than never.
    if (!trigger || !('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    var at = parseFloat(bar.getAttribute('data-threshold'));
    if (isNaN(at)) at = 0.5;

    // The test in one place: true when the trigger is at or above the line,
    // which includes being long past it.
    function past() {
      return trigger.getBoundingClientRect().top < window.innerHeight * at;
    }

    // Already past it before we even ran - a link straight to an anchor further
    // down the article.
    if (past()) {
      reveal();
      return;
    }

    var revealed = false;

    function fire() {
      if (revealed) return;
      revealed = true;
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      reveal();
    }

    // The backstop. An observer only reports a *change* of intersection, and the
    // band here is one pixel tall: jump it in one go - an anchor link, a
    // scrollbar drag, a hard flick - and the trigger goes from below it to above
    // it without ever being inside, so no entry is delivered and the bar stays
    // hidden for the rest of the page. Measured on the live theme before this
    // was added: a single scrollTo past the trigger never revealed it again, and
    // no amount of further scrolling brought it back. One rect read per frame,
    // only until the reveal, then the listener removes itself.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (past()) fire();
      });
    }

    var top = Math.round(at * 100);
    var bottom = 100 - top;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];

          // Not `isIntersecting` alone. The root margin leaves a one-pixel band
          // across the middle of the screen, and an element above it never
          // touches that band again - so a reader who reloaded below the
          // trigger, or came back with the back button, got an observer waiting
          // for an entry that was never coming and no bar for the rest of the
          // page. `rootBounds.bottom` is that band, and a trigger above it has
          // been passed. The source has this fault as well; fixed here on the
          // owner's call, the same way the Regular Adv page was.
          var line = entry.rootBounds
            ? entry.rootBounds.bottom
            : window.innerHeight * at;

          if (entry.isIntersecting || entry.boundingClientRect.top < line) {
            fire();
            return;
          }
        }
      },
      { rootMargin: '-' + top + '% 0px -' + bottom + '% 0px', threshold: 0 }
    );

    observer.observe(trigger);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------------
     Lazy video facade

     Nothing is fetched until the player is near the viewport - the source
     autoplayed both reels on load. Playback pauses again when the player
     scrolls away, and a click toggles it. A reader who has asked for reduced
     motion gets the still and a play button instead of a clip that starts
     moving on its own.
     ---------------------------------------------------------------------- */
  function initVideo(holder) {
    var video = holder.querySelector('video');
    var src = holder.getAttribute('data-src');
    if (!video || !src) return;

    var autoplay = holder.getAttribute('data-autoplay') === 'true' && !reduceMotion;
    var loaded = false;

    video.muted = true;

    function load() {
      if (loaded) return;
      loaded = true;
      video.src = src;
    }

    function play() {
      load();
      var promise = video.play();
      // Rejected when the browser blocks playback. The still stays up, and the
      // play button with it, which is the correct fallback.
      if (promise && promise.catch) promise.catch(function () {});
    }

    video.addEventListener('playing', function () {
      holder.classList.add('gemians-blog-video--playing');
    });
    video.addEventListener('pause', function () {
      holder.classList.remove('gemians-blog-video--playing');
    });

    holder.addEventListener('click', function () {
      if (video.paused) {
        holder.removeAttribute('data-paused-by-reader');
        play();
      } else {
        video.pause();
        holder.setAttribute('data-paused-by-reader', '');
      }
    });

    if (!('IntersectionObserver' in window)) return;

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (autoplay && !holder.hasAttribute('data-paused-by-reader')) play();
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    ).observe(holder);
  }

  function init(scope) {
    initSticky();
    $all('[data-gemians-blog-video]', scope).forEach(initVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-run for a section the client has just edited in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
